import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

export const BASE_UNITS = ["kg", "g", "L", "ml", "unit"];

/** Low-confidence AI-inferred field (catalog import pre-fill) → amber attention border,
 *  same treatment family as low-confidence OCR fields in the invoice review. */
export const lowConf = (line, key) =>
  !!(line.field_confidence && line.field_confidence[key] != null && line.field_confidence[key] < 0.7);

export function StockItemForm({ value, onChange, categories }) {
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label>Name</Label>
        <Input data-testid="stock-item-name" value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Base unit</Label>
          <Select value={value.base_unit} onValueChange={(v) => onChange({ ...value, base_unit: v })}>
            <SelectTrigger data-testid="stock-item-unit"><SelectValue /></SelectTrigger>
            <SelectContent>{BASE_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Category</Label>
          <Select value={value.category_id || "none"} onValueChange={(v) => onChange({ ...value, category_id: v === "none" ? "" : v })}>
            <SelectTrigger data-testid="stock-item-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.department}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

/** Shared stock-item match row fields: match select (candidates + full list + create options)
 *  and purchase unit / pack size / pack unit inputs. Used by the invoice review screen and
 *  the supplier catalog bulk-import review table — keep behaviour identical in both. */
export function MatchAssign({ line, index, stockItems, onPatch, onQuick, onNew, idPrefix = "review-line", alwaysPack = false }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <Select value={line.stock_item_id || ""} onValueChange={(v) => {
        if (v === "__new") { onNew && onNew(index); }
        else if (v === "__quick") { onQuick && onQuick(index); }
        else { const it = stockItems.find((x) => x.id === v); onPatch(index, { stock_item_id: v, stock_item_name: it?.name }); }
      }}>
        <SelectTrigger data-testid={`${idPrefix}-match-${index}`} className={!line.stock_item_id ? "border-[#B33939]" : ""}><SelectValue placeholder="Match stock item…" /></SelectTrigger>
        <SelectContent>
          {(line.candidates || []).map((c) => <SelectItem key={c.stock_item_id} value={c.stock_item_id}>{c.name} (suggested)</SelectItem>)}
          {stockItems.filter((s) => !(line.candidates || []).some((c) => c.stock_item_id === s.id)).map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.base_unit}</SelectItem>)}
          {onQuick && <SelectItem value="__quick" data-testid={`${idPrefix}-quick-${index}`}>Quick add new item (name, unit, cost)</SelectItem>}
          {onNew && <SelectItem value="__new" data-testid={`${idPrefix}-new-${index}`}>Create with full form…</SelectItem>}
        </SelectContent>
      </Select>
      {(line.stock_item_id || alwaysPack) && (
        <div className="grid grid-cols-3 gap-1">
          <Input placeholder="Purch. unit" value={line.purchase_unit || ""} onChange={(e) => onPatch(index, { purchase_unit: e.target.value })} data-testid={`${idPrefix}-punit-${index}`} />
          <Input type="number" step="any" placeholder="Pack size" value={line.pack_size ?? ""} onChange={(e) => onPatch(index, { pack_size: e.target.value })} data-testid={`${idPrefix}-psize-${index}`} className={lowConf(line, "pack_size") ? "border-[#F4A261]" : ""} title={lowConf(line, "pack_size") ? "AI-inferred with low confidence — please verify" : undefined} />
          <Select value={line.pack_unit || "none"} onValueChange={(v) => onPatch(index, { pack_unit: v === "none" ? "" : v })}>
            <SelectTrigger data-testid={`${idPrefix}-ppack-${index}`} className={lowConf(line, "pack_unit") ? "border-[#F4A261]" : ""}><SelectValue placeholder="Pack unit" /></SelectTrigger>
            <SelectContent><SelectItem value="none">—</SelectItem>{BASE_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
